import { PairingNode, rtdbPaths } from '@akpl/shared';
import { Brand, Button, QrCode } from '@akpl/ui-tv';
import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { analytics } from '../analytics';
import { api, APP_VERSION } from '../api';
import { getBridge } from '../bridge';
import { getFirebase } from '../firebase';
import { prefetchHome } from '../queryClient';
import { type AuthMode, type TvStoreApi, useStore } from '../store';
import { useT } from '../useT';
import {
  displayUrl,
  formatCountdown,
  PairingController,
  type PairingDeps,
} from './pairing/controller';
import styles from './screens.module.css';
import type { ScreenProps } from './types';
import { useScreenFocus } from './useScreenFocus';

export type PairingViewStatus =
  'starting' | 'pending' | 'approved' | 'denied' | 'expired' | 'resuming' | 'blocked' | 'error';

/** Everything the TV-01 layout needs; fed by `PairingController` (docs/04 §3 TV-1). */
export interface PairingViewProps {
  /** Empty while no code exists yet (starting / blocked / error before the first code). */
  userCode: string;
  qrUrl: string;
  verificationUrl: string;
  /** "mm:ss" left; null hides the countdown. */
  expiresIn: string | null;
  supportEmail: string;
  status: PairingViewStatus;
  onNewCode: () => void;
}

export const PAIRING_FOCUS = 'pairing-new-code';
const CODE_PLACEHOLDER = '······';

export function PairingView({
  userCode,
  qrUrl,
  verificationUrl,
  expiresIn,
  supportEmail,
  status,
  onNewCode,
}: PairingViewProps) {
  const { t } = useT();
  const statusLine =
    status === 'approved'
      ? t('tv:pairing.approved')
      : status === 'denied'
        ? t('tv:pairing.denied')
        : status === 'expired'
          ? t('tv:pairing.expired')
          : status === 'resuming'
            ? t('tv:pairing.resuming')
            : status === 'starting'
              ? t('tv:pairing.starting')
              : status === 'blocked'
                ? t('tv:pairing.deviceBlocked', { email: supportEmail })
                : status === 'error'
                  ? t('tv:pairing.retrying')
                  : null;
  return (
    <div
      className={`${styles.safe} ${styles.pairing}`}
      data-testid="screen-pairing"
      data-status={status}
    >
      <div className={styles.pairingLeft}>
        <Brand name={t('common:app.name')} />
        <h1 className={styles.h1}>{t('tv:pairing.heading')}</h1>
        <p className={styles.lead}>{t('tv:pairing.enterCodeAt', { url: verificationUrl })}</p>
        <div className={styles.pairingCode} data-testid="user-code">
          {userCode || CODE_PLACEHOLDER}
        </div>
        <div className={styles.pairingMeta}>
          <span className={styles.pairingUrl}>{verificationUrl}</span>
          {expiresIn ? (
            <span className={styles.pairingExpires} data-testid="pairing-countdown">
              {t('tv:pairing.expiresIn', { time: expiresIn })}
            </span>
          ) : null}
        </div>
        <div>
          <Button focusKey={PAIRING_FOCUS} variant="primary" onSelect={onNewCode}>
            {t('tv:pairing.newCode')}
          </Button>
        </div>
        <p className={styles.muted} data-testid="pairing-status" role="status">
          {statusLine ?? t('tv:pairing.help', { email: supportEmail })}
        </p>
      </div>
      <div className={`${styles.panel} ${styles.pairingRight}`}>
        {qrUrl ? <QrCode value={qrUrl} size={520} /> : <div style={{ width: 520, height: 520 }} />}
        <div className={styles.pairingScan}>{t('tv:pairing.scanQr')}</div>
        <div className={styles.muted}>{t('tv:pairing.openCamera')}</div>
      </div>
    </div>
  );
}

const DEFAULT_VERIFICATION_URL = 'https://app.acapella.lol/tv';

/**
 * Wires the controller to the app: `api.pairStart` / `api.pairExchange`, the RTDB listener on
 * `/pairing/{deviceCode}` (or polling in offline-auth mode), `signInWithCustomToken`, and the
 * store (`setAuth` + `setSession`; the Shell auth gate then routes to Home).
 */
export function createTvPairingController(
  store: TvStoreApi,
  { deviceId, authMode }: { deviceId: string; authMode: AuthMode },
): PairingController {
  const { device } = store.getState();
  const bridge = getBridge(APP_VERSION);
  const deps: PairingDeps = {
    api,
    device: {
      id: deviceId,
      platform: device.bridge === 'native' ? 'android_tv' : 'web',
      ...(device.info?.model ? { model: device.info.model } : {}),
      appVersion: APP_VERSION,
    },
    watch: (deviceCode, cb) => {
      const firebase = authMode === 'firebase' ? getFirebase() : null;
      if (!firebase) return null;
      return firebase.listen(
        rtdbPaths.pairing(deviceCode),
        PairingNode,
        (node, meta) => {
          if (meta.error) bridge.bridge.log('warn', `pairing node invalid: ${meta.error.message}`);
          cb(node);
        },
        (err) => bridge.bridge.log('warn', `pairing listener error: ${err.message}`),
      );
    },
    signIn:
      authMode === 'firebase'
        ? async (customToken) => {
            const firebase = getFirebase();
            if (!firebase) throw new Error('Firebase client not loaded');
            const user = await firebase.signInWithCustomToken(customToken);
            return { uid: user.uid };
          }
        : null,
    onPaired: (res, uid) => {
      const s = store.getState();
      s.setAuth({ status: 'signed_in', uid, user: res.user, features: res.session.features });
      s.setSession(res.session);
      // UX-110: overlap the Home request with the hand-off instead of starting it when the
      // screen mounts — that wait was 3.5–13 s of a bare "Cargando…".
      prefetchHome();
      analytics.identify(uid);
      analytics.track('tv_paired', { did: deviceId, sid: res.session.id });
      analytics.track('login', { method: 'tv_custom_token' });
    },
    storage: typeof localStorage === 'undefined' ? null : localStorage,
    log: (level, message) => bridge.bridge.log(level, message),
  };
  return new PairingController(deps);
}

/** TV-1 Pairing (docs/04 §3): start → code/QR/countdown → approved → exchange → sign-in → Home. */
export default function Pairing(_props: ScreenProps<'pairing'>) {
  const supportEmail = useStore((s) => s.config?.supportEmail) || 'support@acapella.lol';
  const deviceId = useStore((s) => s.device.id);
  const authMode = useStore((s) => s.authMode);
  // A new device id / auth mode (only possible across boots) gets a fresh controller.
  const controller = useMemo(
    () => createTvPairingController(useStore, { deviceId, authMode }),
    [deviceId, authMode],
  );
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getState,
    controller.getState,
  );

  useEffect(() => {
    void controller.start();
    return () => controller.stop();
  }, [controller]);
  useScreenFocus('pairing', PAIRING_FOCUS);

  const { phase } = state;
  const code = 'code' in phase ? phase.code : null;
  const status: PairingViewStatus =
    phase.kind === 'idle' || phase.kind === 'starting'
      ? 'starting'
      : phase.kind === 'done'
        ? 'approved'
        : phase.kind;
  const expiresIn = phase.kind === 'pending' ? formatCountdown(phase.expiresAt - state.now) : null;

  return (
    <PairingView
      userCode={code?.userCode ?? ''}
      qrUrl={code?.qrUrl ?? ''}
      verificationUrl={displayUrl(code?.verificationUrl ?? DEFAULT_VERIFICATION_URL)}
      expiresIn={expiresIn}
      supportEmail={supportEmail}
      status={status}
      onNewCode={() => void controller.newCode()}
    />
  );
}
